import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/Layout';
import { toast } from 'react-toastify';
import './ClientDashboard.css';
import './OnlineClassesPage.css';

const OnlineClassesPage = () => {
  const [classes, setClasses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classDetail, setClassDetail] = useState(null);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch('/api/onlineclasses/categories');
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  }, []);

  const fetchClasses = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedDifficulty) params.append('difficulty', selectedDifficulty);
      if (selectedType) params.append('type', selectedType);
      
      const response = await fetch(`/api/onlineclasses?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClasses(data);
      }
    } catch (error) {
      console.error('Erro ao carregar aulas:', error);
    }
  }, [selectedCategory, selectedDifficulty, selectedType]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchCategories();
      await fetchClasses();
      setLoading(false);
    };
    loadData();
  }, [fetchCategories, fetchClasses]);

  const openClassDetail = async (classItem) => {
    try {
      const response = await fetch(`/api/onlineclasses/${classItem.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClassDetail(data);
        setSelectedClass(classItem);
      }
    } catch (error) {
      toast.error('Erro ao carregar detalhes da aula');
    }
  };

  const joinClass = async (classId) => {
    try {
      const response = await fetch(`/api/onlineclasses/${classId}/join`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'A iniciar aula...');
        // Refetch to update watched status
        fetchClasses();
      } else {
        const error = await response.json();
        toast.error(error.message || 'Erro ao entrar na aula');
      }
    } catch (error) {
      toast.error('Erro ao entrar na aula');
    }
  };

  const likeClass = async (classId) => {
    try {
      const response = await fetch(`/api/onlineclasses/${classId}/like`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        // Refresh detail
        if (classDetail) {
          openClassDetail(selectedClass);
        }
      }
    } catch (error) {
      console.error('Erro ao dar like:', error);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const getDifficultyLabel = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy': return 'Fácil';
      case 'medium': return 'Médio';
      case 'hard': return 'Difícil';
      default: return difficulty || 'N/A';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 0:
      case 'Video': return '📹 Vídeo';
      case 1:
      case 'Live': return '🔴 Ao Vivo';
      default: return type;
    }
  };

  if (loading) {
    return (
      <DashboardLayout userType="client">
        <div className="data-card">
          <div className="empty-state">
            <div className="empty-icon">⏳</div>
            <h3 className="empty-title">A carregar...</h3>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="client">
      <div className="online-classes-page">
        {/* Featured Banner */}
        <div className="featured-section">
          <div className="featured-banner">
            <div className="featured-content">
              <span className="featured-badge">🔥 Em Destaque</span>
              <h2 className="featured-title">Aulas Online a Qualquer Hora</h2>
              <p className="featured-desc">
                Treine em casa com os melhores instrutores. Aulas gravadas e ao vivo disponíveis 24/7.
              </p>
              <div className="featured-meta">
                <span>🎬 {classes.length} Aulas Disponíveis</span>
                <span>🏋️ {categories.length} Categorias</span>
                <span>⭐ Instrutores Certificados</span>
              </div>
            </div>
          </div>
        </div>

        <div className="data-card">
          <div className="card-header">
            <h3 className="card-title">🎬 Aulas Online</h3>
          </div>

          {/* Categories */}
          <div className="categories-scroll">
            <button 
              className={`category-chip ${selectedCategory === '' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('')}
            >
              🏠 Todas
            </button>
            {categories.map(cat => (
              <button 
                key={cat.id}
                className={`category-chip ${selectedCategory === cat.name ? 'active' : ''}`}
                onClick={() => setSelectedCategory(cat.name)}
              >
                {cat.icon} {cat.name}
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="filters-row">
            <select 
              className="filter-select"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <option value="">Todos os tipos</option>
              <option value="Video">📹 Vídeos</option>
              <option value="Live">🔴 Ao Vivo</option>
            </select>
            <select 
              className="filter-select"
              value={selectedDifficulty}
              onChange={(e) => setSelectedDifficulty(e.target.value)}
            >
              <option value="">Todas as dificuldades</option>
              <option value="easy">🟢 Fácil</option>
              <option value="medium">🟡 Médio</option>
              <option value="hard">🔴 Difícil</option>
            </select>
          </div>

          {/* Classes Grid */}
          {classes.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📹</div>
              <h3 className="empty-title">Sem aulas disponíveis</h3>
              <p className="empty-desc">Ainda não existem aulas online nesta categoria.</p>
            </div>
          ) : (
            <div className="classes-grid">
              {classes.map(classItem => (
                <div 
                  key={classItem.id} 
                  className="class-card"
                  onClick={() => openClassDetail(classItem)}
                >
                  <div className="class-thumbnail">
                    {classItem.thumbnailUrl ? (
                      <img src={classItem.thumbnailUrl} alt={classItem.title} />
                    ) : (
                      <span className="class-thumbnail-placeholder">🎬</span>
                    )}
                    <span className="class-duration">{classItem.durationMinutes} min</span>
                    {classItem.isLive && <span className="class-live-badge">🔴 AO VIVO</span>}
                    {classItem.hasCompleted && <span className="class-watched-badge">✓ Concluída</span>}
                  </div>
                  <div className="class-content">
                    <span className="class-category-badge">{classItem.category}</span>
                    <h4 className="class-title">{classItem.title}</h4>
                    <div className="class-instructor">
                      <div className="instructor-photo">
                        {classItem.instructorPhoto ? (
                          <img src={classItem.instructorPhoto} alt={classItem.instructorName} />
                        ) : (
                          getInitials(classItem.instructorName)
                        )}
                      </div>
                      <span className="instructor-name">{classItem.instructorName}</span>
                    </div>
                    <div className="class-meta">
                      <div className="class-stats">
                        <span className="class-stat">👁 {classItem.viewCount || 0}</span>
                        <span className="class-stat">❤️ {classItem.likeCount || 0}</span>
                        {classItem.averageRating > 0 && (
                          <span className="class-stat">⭐ {classItem.averageRating?.toFixed(1)}</span>
                        )}
                      </div>
                      <span className={`class-difficulty ${classItem.difficulty?.toLowerCase()}`}>
                        {getDifficultyLabel(classItem.difficulty)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Class Detail Modal */}
      {selectedClass && classDetail && (
        <div className="class-modal" onClick={() => { setSelectedClass(null); setClassDetail(null); }}>
          <div className="class-modal-content" onClick={e => e.stopPropagation()}>
            <div className="class-modal-video">
              {classDetail.videoUrl ? (
                <video controls poster={classDetail.thumbnailUrl}>
                  <source src={classDetail.videoUrl} type="video/mp4" />
                </video>
              ) : (
                <div className="video-placeholder">
                  <div className="video-placeholder-icon">🎬</div>
                  <p>Vídeo não disponível para pré-visualização</p>
                </div>
              )}
            </div>
            <div className="class-modal-body">
              <div className="class-modal-header">
                <h2 className="class-modal-title">{classDetail.title}</h2>
                <button 
                  className="class-modal-close"
                  onClick={() => { setSelectedClass(null); setClassDetail(null); }}
                >
                  ×
                </button>
              </div>
              
              <div className="class-modal-meta">
                <span className="meta-item">🏋️ {classDetail.category}</span>
                <span className="meta-item">⏱ {classDetail.durationMinutes} min</span>
                <span className="meta-item">{getTypeLabel(classDetail.type)}</span>
                <span className="meta-item">👁 {classDetail.viewCount} visualizações</span>
              </div>

              <p className="class-modal-desc">{classDetail.description}</p>

              <div className="class-instructor" style={{ marginBottom: '20px' }}>
                <div className="instructor-photo">
                  {classDetail.instructorPhoto ? (
                    <img src={classDetail.instructorPhoto} alt={classDetail.instructorName} />
                  ) : (
                    getInitials(classDetail.instructorName)
                  )}
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                    {classDetail.instructorName}
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Instrutor
                  </div>
                </div>
              </div>

              <div className="class-modal-actions">
                <button 
                  className="modal-btn primary"
                  onClick={() => joinClass(classDetail.id)}
                >
                  ▶️ Começar Aula
                </button>
                <button 
                  className={`modal-btn like ${classDetail.participantInfo?.liked ? 'liked' : ''}`}
                  onClick={() => likeClass(classDetail.id)}
                >
                  ❤️ {classDetail.likeCount || 0}
                </button>
                <button 
                  className="modal-btn outline"
                  onClick={() => { setSelectedClass(null); setClassDetail(null); }}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default OnlineClassesPage;
